import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, NavLink } from "react-router-dom";
import { Button } from "./components/ui/button";
import { Card } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { NavigationMenu, NavigationMenuItem, NavigationMenuList } from "./components/ui/navigation-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./components/ui/tooltip";
import { Separator } from "./components/ui/separator";
import { PawPrint, Instagram, Phone, Mail, Facebook } from "lucide-react";
import About from "./pages/About";
import Team from "./pages/Team";
import Services from "./pages/Services";
import { SITE } from "./mock";
import { Toaster, toast } from "sonner";
import { CornerLeaves } from "./components/CornerLeaves";
import { OpenMouthReveal } from "./components/OpenMouthReveal";
import { StructuredData } from "./components/StructuredData";
import { SeoMeta } from "./components/SeoMeta";

function App() {
  return (
    <div className="App min-h-screen bg-background lux-wrapper">
      <BrowserRouter>
        <StructuredData />
        <Header />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/team" element={<Team />} />
          <Route path="/services" element={<Services />} />
        </Routes>
        <Footer />
        <CornerLeaves />
        <OpenMouthReveal />
      </BrowserRouter>
      <Toaster richColors position="top-center" />
    </div>
  );
}

export default App;

const Header = () => {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-border/50">
      <div className="mx-auto max-w-6xl px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={SITE.logoUrl} alt="Logo fyzioterapie Zdravé packy" className="h-10 w-10 object-contain header-logo" />
          <span className="font-semibold text-lg">{SITE.name}</span>
        </Link>
        <NavigationMenu>
          <NavigationMenuList className="gap-6">
            <NavigationMenuItem>
              <NavLink to="/" className={({isActive}) => `text-sm ${isActive ? "text-blue-700" : "text-foreground/80"}`}>Domů</NavLink>
            </NavigationMenuItem>
            <NavigationMenuItem>
              <NavLink to="/about" className={({isActive}) => `text-sm ${isActive ? "text-blue-700" : "text-foreground/80"}`}>O mně</NavLink>
            </NavigationMenuItem>
            <NavigationMenuItem>
              <NavLink to="/team" className={({isActive}) => `text-sm ${isActive ? "text-blue-700" : "text-foreground/80"}`}>Náš tým</NavLink>
            </NavigationMenuItem>
            <NavigationMenuItem>
              <NavLink to="/services" className={({isActive}) => `text-sm ${isActive ? "text-blue-700" : "text-foreground/80"}`}>Služby</NavLink>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>
        <div className="hidden md:flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button asChild variant="outline">
                  <a href={`tel:${SITE.contacts.phone}`}><Phone className="mr-2 h-4 w-4" />{SITE.contacts.phone}</a>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Volat nyní</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white">
            <a href={`mailto:${SITE.contacts.email}?subject=Zdravé packy - poptávka`}>Napsat e‑mail</a>
          </Button>
        </div>
      </div>
    </header>
  );
};

const Footer = () => {
  return (
    <footer className="mt-16 border-t">
      <div className="mx-auto max-w-6xl px-4 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <PawPrint className="h-4 w-4" /> <span>{SITE.name} • {new Date().getFullYear()}</span>
        </div>
        <div className="flex items-center gap-3">
          <Button size="sm" variant="outline" asChild>
            <a href={SITE.contacts.facebook} target="_blank" rel="noreferrer noopener"><Facebook className="h-4 w-4 mr-2" />Facebook</a>
          </Button>
          <Button size="sm" variant="outline" asChild>
            <a href={SITE.contacts.instagram} target="_blank" rel="noreferrer noopener"><Instagram className="h-4 w-4 mr-2" />Instagram</a>
          </Button>
          <Button size="sm" variant="outline" asChild>
            <a href={`mailto:${SITE.contacts.email}`}><Mail className="h-4 w-4 mr-2" />Kontaktovat</a>
          </Button>
        </div>
      </div>
    </footer>
  );
};

function Home() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [result, setResult] = useState("");

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("zp-callbacks") || "[]");
    if (saved.length > 0) {
    }
  }, []);

  const submitForm = async (event) => {
    event.preventDefault();
    setResult("Sending...");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          access_key: "abb5ecbb-40a9-4e24-af95-157ee41e42f7",
          name: name,
          phone: phone,
          subject: "Kontakt z webu Zdravé packy"
        })
      });

      const data = await response.json();
      if (data.success) {
        setResult("Success");
        setName("");
        setPhone("");
        toast.success("Děkujeme! Ozveme se vám co nejdříve.");
      } else {
        setResult("Error");
        toast.error("Něco se pokazilo. Upozorněte nás prosím.");
      }
    } catch (error) {
      setResult("Error");
      toast.error("Něco se pokazilo. Upozorněte nás prosím.");
    }
  };

  return (
    <div className="relative hero-gradient">
      <SeoMeta 
        title="Zdravé packy - Fyzioterapie zvířat"
        description="Profesionální fyzioterapie a rehabilitace zvířat v Novém Bohumíně. Odborná péče pro lepší pohyblivost a zdraví vašich mazlíčků. Zarezervujte si termín!"
        url="https://www.zdravepacky.cz"
      />
      <section className="relative overflow-hidden animate-in">
        <div className="mx-auto max-w-6xl px-4 pt-12 pb-10 grid md:grid-cols-2 gap-8 items-center">
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-600/10 text-blue-700 border border-blue-600/20">
              Fyzioterapie zvířat Nový Bohumín
            </div>
            <h1 className="mt-4 text-4xl md:text-6xl brand-serif leading-tight">
              {SITE.name}
            </h1>
            <p className="mt-3 text-muted-foreground max-w-prose">
              Krůček po krůčku.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white btn-pulse">
                <a href={`tel:${SITE.contacts.phone}`}><Phone className="h-4 w-4 mr-2" />Objednat termín</a>
              </Button>
              <Button asChild variant="outline">
                <a href="/services">Zobrazit služby</a>
              </Button>
            </div>
          </div>
          <div className="relative">
            <div className="glass rounded-2xl overflow-hidden flex items-center justify-center p-8 card-hover">
              <img src={SITE.logoUrl} alt="Logo Zdravé packy" className="w-full max-h-[460px] object-contain" />
            </div>
          </div>
        </div>
      </section>

      <Separator className="opacity-50" />

      <section className="py-10 animate-in">
        <div className="mx-auto max-w-6xl px-4 grid md:grid-cols-2 gap-8 items-start">
          <div className="glass rounded-xl p-6 card-hover">
            <h2 className="text-2xl font-semibold">Kontakt</h2>
            <p className="text-muted-foreground mt-1">Ozvěte se mi – domluvíme vše potřebné.</p>
            <div className="mt-4 grid gap-3">
              <Button variant="outline" asChild>
                <a href={SITE.contacts.facebook} target="_blank" rel="noreferrer noopener"><Facebook className="h-4 w-4 mr-2" />Facebook</a>
              </Button>
              <Button variant="outline" asChild>
                <a href={SITE.contacts.instagram} target="_blank" rel="noreferrer noopener"><Instagram className="h-4 w-4 mr-2" />Instagram</a>
              </Button>
              <Button variant="outline" asChild>
                <a href={`mailto:${SITE.contacts.email}`}><Mail className="h-4 w-4 mr-2" />{SITE.contacts.email}</a>
              </Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" asChild>
                <a href={`tel:${SITE.contacts.phone}`}><Phone className="h-4 w-4 mr-2" />{SITE.contacts.phone}</a>
              </Button>
            </div>
          </div>

          <div className="glass rounded-xl p-6 card-hover">
            <h2 className="text-2xl font-semibold">Zavoláme vám zpět</h2>
            <p className="text-muted-foreground mt-1">Nechte na sebe kontakt, ozveme se vám co nejdříve.</p>
            <form onSubmit={submitForm} className="mt-4 grid gap-4">
              <div className="grid gap-1">
                <Label htmlFor="name">Jméno</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Vaše jméno" />
              </div>
              <div className="grid gap-1">
                <Label htmlFor="phone">Telefon</Label>
                <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Např. 732 304 063" />
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">Odeslat</Button>
                <Button type="button" variant="outline" onClick={() => { setName(""); setPhone(""); }}>Vymazat</Button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}